import React, { useState, useMemo } from 'react';
import { PlacedBlock, SocketConnection, BlockSystem, PartAttribute } from '@/app/types/cad';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { X, Trash2, Edit2, Search, Database, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

interface ParameterManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  systems: BlockSystem[];
  onBlockAttributeUpdate: (blockId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onBlockAttributeDelete: (blockId: string, attrId: string) => void;
  onConnectionAttributeUpdate: (connectionId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onConnectionAttributeDelete: (connectionId: string, attrId: string) => void;
  onSystemAttributeUpdate: (systemId: string, attrId: string, updates: Partial<PartAttribute>) => void;
  onSystemAttributeDelete: (systemId: string, attrId: string) => void;
}

interface AttributeInfo {
  id: string;
  name: string;
  value: string;
  type: PartAttribute['type'];
  unit?: string;
  sourceType: 'block' | 'connection' | 'system';
  sourceId: string;
  sourceName: string;
}

export function ParameterManagementDialog({
  open,
  onOpenChange,
  blocks,
  connections,
  systems,
  onBlockAttributeUpdate,
  onBlockAttributeDelete,
  onConnectionAttributeUpdate,
  onConnectionAttributeDelete,
  onSystemAttributeUpdate,
  onSystemAttributeDelete,
}: ParameterManagementDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAttr, setEditingAttr] = useState<AttributeInfo | null>(null);
  const [editName, setEditName] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Collect all attributes from all sources
  const allAttributes = useMemo(() => {
    const attrs: AttributeInfo[] = [];

    // From blocks
    blocks.forEach(block => {
      if (block.blockData.attributes) {
        block.blockData.attributes.forEach(attr => {
          attrs.push({
            id: attr.id,
            name: attr.name,
            value: attr.value,
            type: attr.type,
            unit: attr.unit,
            sourceType: 'block',
            sourceId: block.id,
            sourceName: block.blockData.name,
          });
        });
      }
    });

    // From connections
    connections.forEach(connection => {
      if (connection.attributes) {
        connection.attributes.forEach(attr => {
          attrs.push({
            id: attr.id,
            name: attr.name,
            value: attr.value,
            type: attr.type,
            unit: attr.unit,
            sourceType: 'connection',
            sourceId: connection.id,
            sourceName: `Verbindung (DN${connection.dn})`,
          });
        });
      }
    });

    // From systems
    systems.forEach(system => {
      if (system.attributes) {
        system.attributes.forEach(attr => {
          attrs.push({
            id: attr.id,
            name: attr.name,
            value: attr.value,
            type: attr.type,
            unit: attr.unit,
            sourceType: 'system',
            sourceId: system.id,
            sourceName: `System: ${system.name}`,
          });
        });
      }
    });

    return attrs;
  }, [blocks, connections, systems]);

  // Filter attributes by search term
  const filteredAttributes = useMemo(() => {
    if (!searchTerm) return allAttributes;
    const lower = searchTerm.toLowerCase();
    return allAttributes.filter(
      attr =>
        attr.name.toLowerCase().includes(lower) ||
        attr.value.toLowerCase().includes(lower) ||
        attr.sourceName.toLowerCase().includes(lower)
    );
  }, [allAttributes, searchTerm]);

  // Group by attribute name
  const groupedAttributes = useMemo(() => {
    const groups = new Map<string, AttributeInfo[]>();
    filteredAttributes.forEach(attr => {
      const existing = groups.get(attr.name) || [];
      existing.push(attr);
      groups.set(attr.name, existing);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredAttributes]);

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(groupedAttributes.map(([name]) => name)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleEdit = (attr: AttributeInfo) => {
    setEditingAttr(attr);
    setEditName(attr.name);
    setEditValue(attr.value);
    setEditUnit(attr.unit || '');
  };

  const handleSaveEdit = () => {
    if (!editingAttr) return;

    const updates: Partial<PartAttribute> = {
      name: editName,
      value: editValue,
      unit: editUnit || undefined,
    };

    if (editingAttr.sourceType === 'block') {
      onBlockAttributeUpdate(editingAttr.sourceId, editingAttr.id, updates);
    } else if (editingAttr.sourceType === 'connection') {
      onConnectionAttributeUpdate(editingAttr.sourceId, editingAttr.id, updates);
    } else if (editingAttr.sourceType === 'system') {
      onSystemAttributeUpdate(editingAttr.sourceId, editingAttr.id, updates);
    }

    toast.success('Attribut aktualisiert');
    setEditingAttr(null);
  };

  const handleDelete = (attr: AttributeInfo) => {
    if (!confirm(`Attribut "${attr.name}" aus ${attr.sourceName} löschen?`)) {
      return;
    }

    if (attr.sourceType === 'block') {
      onBlockAttributeDelete(attr.sourceId, attr.id);
    } else if (attr.sourceType === 'connection') {
      onConnectionAttributeDelete(attr.sourceId, attr.id);
    } else if (attr.sourceType === 'system') {
      onSystemAttributeDelete(attr.sourceId, attr.id);
    }

    toast.success('Attribut gelöscht');
  };

  const handleBulkRename = (oldName: string) => {
    const newName = prompt(`Alle Attribute "${oldName}" umbenennen in:`, oldName);
    if (!newName || newName === oldName) return;

    const attrsToRename = allAttributes.filter(a => a.name === oldName);
    let count = 0;

    attrsToRename.forEach(attr => {
      if (attr.sourceType === 'block') {
        onBlockAttributeUpdate(attr.sourceId, attr.id, { name: newName });
      } else if (attr.sourceType === 'connection') {
        onConnectionAttributeUpdate(attr.sourceId, attr.id, { name: newName });
      } else if (attr.sourceType === 'system') {
        onSystemAttributeUpdate(attr.sourceId, attr.id, { name: newName });
      }
      count++;
    });

    toast.success(`${count} Attribute umbenannt: "${oldName}" → "${newName}"`);
  };

  const handleBulkDelete = (name: string) => {
    const attrsToDelete = allAttributes.filter(a => a.name === name);
    if (!confirm(`${attrsToDelete.length} Attribute mit Namen "${name}" löschen?`)) {
      return;
    }

    let count = 0;
    attrsToDelete.forEach(attr => {
      if (attr.sourceType === 'block') {
        onBlockAttributeDelete(attr.sourceId, attr.id);
      } else if (attr.sourceType === 'connection') {
        onConnectionAttributeDelete(attr.sourceId, attr.id);
      } else if (attr.sourceType === 'system') {
        onSystemAttributeDelete(attr.sourceId, attr.id);
      }
      count++;
    });

    toast.success(`${count} Attribute gelöscht`);
  };

  if (!open) return null;

  const typeDisplayName = (type: PartAttribute['type']) => {
    switch (type) {
      case 'dn': return 'DN';
      case 'pn': return 'PN';
      case 'text': return 'Text';
      case 'number': return 'Zahl';
      case 'dimension': return 'Maß';
      case 'angle': return 'Winkel';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => onOpenChange(false)}>
      <div 
        className="rounded-lg shadow-xl w-full max-w-[1000px] h-[85vh] flex flex-col m-4"
        style={{ backgroundColor: 'var(--md-surface-container-low)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div 
          className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6" style={{ color: 'var(--md-primary)' }} />
            <div>
              <h2 className="text-xl font-semibold" style={{ color: 'var(--md-on-surface)' }}>
                Parameter-Management
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--md-on-surface-variant)' }}>
                Zentrale Verwaltung aller Projekt-Attribute
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Stats & Actions - Fixed */}
        <div 
          className="px-6 py-3 border-b flex items-center justify-between flex-shrink-0 flex-wrap gap-2"
          style={{ 
            borderColor: 'var(--md-outline-variant)',
            backgroundColor: 'var(--md-surface-container)'
          }}
        >
          <div className="flex gap-6 text-sm flex-wrap" style={{ color: 'var(--md-on-surface-variant)' }}>
            <div>
              <span className="font-semibold text-lg" style={{ color: 'var(--md-on-surface)' }}>
                {allAttributes.length}
              </span>
              {' '}Attribute gesamt
            </div>
            <div className="h-5 w-px" style={{ backgroundColor: 'var(--md-outline-variant)' }} />
            <div>📦 {allAttributes.filter(a => a.sourceType === 'block').length} Blöcke</div>
            <div>🔌 {allAttributes.filter(a => a.sourceType === 'connection').length} Verbindungen</div>
            <div>🔗 {allAttributes.filter(a => a.sourceType === 'system').length} Systeme</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={expandAll}>
              Alle aufklappen
            </Button>
            <Button size="sm" variant="ghost" onClick={collapseAll}>
              Alle zuklappen
            </Button>
          </div>
        </div>

        {/* Search - Fixed */}
        <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--md-on-surface-variant)' }} />
            <Input
              placeholder="Suche nach Name, Wert oder Quelle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
              {filteredAttributes.length} von {allAttributes.length} Attributen gefunden
            </div>
          )}
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="px-6 py-4">
              {groupedAttributes.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {searchTerm ? 'Keine Attribute gefunden' : 'Keine Attribute im Projekt'}
                  </p>
                  {searchTerm && (
                    <p className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                      Versuchen Sie einen anderen Suchbegriff
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedAttributes.map(([name, attrs]) => {
                    const isExpanded = expandedGroups.has(name);
                    
                    return (
                      <div 
                        key={name}
                        className="border rounded-lg overflow-hidden"
                        style={{ borderColor: 'var(--md-outline-variant)' }}
                      >
                        {/* Group Header - Clickable */}
                        <div 
                          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: 'var(--md-surface-container)' }}
                          onClick={() => toggleGroup(name)}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" style={{ color: 'var(--md-on-surface-variant)' }} />
                            ) : (
                              <ChevronRight className="w-4 h-4" style={{ color: 'var(--md-on-surface-variant)' }} />
                            )}
                            <div>
                              <div className="font-semibold" style={{ color: 'var(--md-on-surface)' }}>
                                {name}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: 'var(--md-on-surface-variant)' }}>
                                {attrs.length} {attrs.length === 1 ? 'Vorkommen' : 'Vorkommen'}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleBulkRename(name)}
                              title="Alle umbenennen"
                              className="text-xs"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Alle umbenennen
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleBulkDelete(name)}
                              title="Alle löschen"
                              className="text-xs"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Alle löschen
                            </Button>
                          </div>
                        </div>

                        {/* Instances - Collapsible */}
                        {isExpanded && (
                          <div className="divide-y" style={{ borderColor: 'var(--md-outline-variant)' }}>
                            {attrs.map((attr) => (
                              <div 
                                key={`${attr.sourceId}-${attr.id}`}
                                className="px-4 py-3"
                                style={{ backgroundColor: 'var(--md-surface)' }}
                              >
                                {editingAttr?.id === attr.id && editingAttr?.sourceId === attr.sourceId ? (
                                  // Edit Mode
                                  <div className="space-y-3">
                                    <div className="grid grid-cols-3 gap-3">
                                      <div>
                                        <Label className="text-xs mb-1">Name</Label>
                                        <Input
                                          value={editName}
                                          onChange={(e) => setEditName(e.target.value)}
                                          className="h-9"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs mb-1">Wert</Label>
                                        <Input
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          className="h-9"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs mb-1">Einheit</Label>
                                        <Input
                                          value={editUnit}
                                          onChange={(e) => setEditUnit(e.target.value)}
                                          className="h-9"
                                          placeholder="optional"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" onClick={handleSaveEdit}>
                                        <Save className="w-3 h-3 mr-1" />
                                        Speichern
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingAttr(null)}>
                                        Abbrechen
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  // View Mode
                                  <div className="flex items-center gap-4">
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <div className="text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                                          Wert
                                        </div>
                                        <div className="font-mono font-medium" style={{ color: 'var(--md-on-surface)' }}>
                                          {attr.value} {attr.unit && <span className="text-xs">{attr.unit}</span>}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                                          Typ
                                        </div>
                                        <div className="text-xs px-2 py-1 rounded inline-block" style={{ 
                                          backgroundColor: 'var(--md-surface-container-high)',
                                          color: 'var(--md-on-surface)'
                                        }}>
                                          {typeDisplayName(attr.type)}
                                        </div>
                                      </div>
                                      <div className="col-span-2">
                                        <div className="text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                                          Quelle
                                        </div>
                                        <div className="truncate text-sm" title={attr.sourceName}>
                                          {attr.sourceType === 'block' && '📦 '}
                                          {attr.sourceType === 'connection' && '🔌 '}
                                          {attr.sourceType === 'system' && '🔗 '}
                                          {attr.sourceName}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEdit(attr)}
                                        title="Bearbeiten"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete(attr)}
                                        title="Löschen"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Footer - Fixed */}
        <div 
          className="px-6 py-4 border-t flex justify-between items-center flex-shrink-0"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <div className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
            Tipp: Klicken Sie auf einen Attribut-Namen, um alle Vorkommen zu sehen
          </div>
          <Button onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
}
